/**
 * 「국외 허용 IP」(SPEC-SENDCERT-001 D-138) 화면 규칙. 서버(inapi)도 같은 검사를 하고 같은 문구로 400 을 준다.
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/geoAllowIp.test.mjs`).
 */
import dayjs from "dayjs";

export const CIDR_REQUIRED_MESSAGE = "IP 주소나 대역을 입력하세요.";
export const CIDR_INVALID_MESSAGE = "올바른 IP 주소나 대역(CIDR)이 아닙니다.";
export const CIDR_TOO_WIDE_MESSAGE =
  "대역이 너무 넓습니다. IPv4 는 /16, IPv6 는 /48 까지 입력할 수 있습니다.";
export const UNTIL_REQUIRED_MESSAGE = "끝 날짜를 고르세요.";
export const UNTIL_PAST_MESSAGE = "끝 날짜는 지금 이후여야 합니다.";
export const FROM_AFTER_UNTIL_MESSAGE = "시작일은 끝 날짜보다 늦을 수 없습니다.";
export const NO_COMPANY_MESSAGE = "회사를 하나 이상 고르세요.";

const OCTET = "(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)";
const IPV4 = new RegExp(`^${OCTET}(\\.${OCTET}){3}$`);

// IPv6 는 브라우저·node 가 같이 가진 URL 파서로 검사한다(:: 줄임·IPv4 꼬리 규칙을 정규식으로 옮기지 않음).
// 16진수·콜론·점만 받아 "]" 로 주소 밖에 글자를 붙이는 입력을 먼저 막는다
const isIpv6 = (ip) => {
  if (!/^[0-9a-fA-F:.]+$/.test(ip)) return false;
  try {
    new URL(`http://[${ip}]/`);
    return true;
  } catch {
    return false;
  }
};

/** URL 파서가 정규화한 압축형(점 꼬리도 16진수로 바뀜)을 16비트 8칸으로 */
const v6Groups = (ip) => {
  const [head, tail] = new URL(`http://[${ip}]/`).hostname.slice(1, -1).split("::");
  const h = head ? head.split(":") : [];
  const t = tail ? tail.split(":") : [];
  return [...h, ...Array(8 - h.length - t.length).fill("0"), ...t].map((g) => parseInt(g, 16));
};

// ::ffff:0:0/96 앞 6칸. node BlockList 는 IPv4 접속을 이 영역의 IPv6 대역에도 맞춰 보므로
// 이 영역과 겹치는 IPv6 대역은 IPv4 대역으로 보고 넓이를 잰다(::/48 처럼 덮으면 IPv4 전체)
const MAPPED = [0, 0, 0, 0, 0, 0xffff];
const overlapsMapped = (ip, bits) => {
  const groups = v6Groups(ip);
  const n = Math.min(bits, 96);
  return MAPPED.every((m, i) => {
    const mask = (0xffff << (16 - Math.min(16, Math.max(0, n - i * 16)))) & 0xffff;
    return (groups[i] & mask) === (m & mask);
  });
};

/** IP 하나 또는 CIDR 대역 검사. 괜찮으면 "", 아니면 서버와 같은 안내 문구 */
export const cidrError = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return CIDR_REQUIRED_MESSAGE;
  const [ip, prefix, extra] = text.split("/");
  if (extra !== undefined) return CIDR_INVALID_MESSAGE;
  if (prefix !== undefined && !/^\d{1,3}$/.test(prefix)) return CIDR_INVALID_MESSAGE;
  const v6 = ip.includes(":");
  if (v6 ? !isIpv6(ip) : !IPV4.test(ip)) return CIDR_INVALID_MESSAGE;
  const max = v6 ? 128 : 32;
  const bits = prefix === undefined ? max : Number(prefix);
  if (bits > max) return CIDR_INVALID_MESSAGE;
  // IPv4 매핑 영역과 겹치면 IPv4 /16 기준 = IPv6 /112(96+16) 이상만
  const min = !v6 ? 16 : overlapsMapped(ip, bits) ? 96 + 16 : 48;
  return bits < min ? CIDR_TOO_WIDE_MESSAGE : "";
};

/** 고른 끝 날짜(dayjs)를 보낼 시각으로: 그날 끝(이 컴퓨터 시간)까지 허용 */
export const untilOfDay = (day) => day.endOf("day");

/** 끝 날짜 검사. 오늘도 고를 수 있고(그날 끝까지) 상한은 없다(운영자가 정함) */
export const untilError = (day, now = dayjs()) => {
  if (!day) return UNTIL_REQUIRED_MESSAGE;
  return untilOfDay(day).isAfter(now) ? "" : UNTIL_PAST_MESSAGE;
};

/** 표 「기간」 칸의 남은 일수. 날짜(자정) 기준으로 센다. 끝났으면 만료 */
export const remainOf = (until, now = dayjs()) => {
  const end = dayjs(until);
  if (!until || !end.isValid() || !end.isAfter(now)) {
    return { expired: true, text: "만료" };
  }
  const days = end.startOf("day").diff(now.startOf("day"), "day");
  return { expired: false, text: days ? `${days}일 남음` : "오늘까지" };
};

/**
 * 날짜를 그대로 두고 저장·승인하면 원래 끝 시각을 다시 보낸다(이력에 「기간」이 바뀐 것처럼 남지 않게).
 * 원래 값이 없거나 이미 지났거나 다른 날을 고르면 고른 날 끝
 */
export const untilBody = (original, day, now = dayjs()) =>
  original && dayjs(original).isAfter(now) && day.isSame(dayjs(original), "day")
    ? original
    : untilOfDay(day).toISOString();

/** 시작일(D-144): 고른 날 0시(이 컴퓨터 시간)부터. 원래 값과 같은 날이면 원래 시각을 다시 보낸다(untilBody 와 같은 이유) */
export const fromBody = (original, day) =>
  original && day.isSame(dayjs(original), "day") ? original : day.startOf("day").toISOString();

/**
 * 기간 검사(D-144). 무기한이면 끝 날짜를 묻지 않는다. 시작일은 끝 날짜와 같은 날까지(그날 0시~끝)
 * f = { from, until, indefinite } (날짜는 dayjs)
 */
export const periodError = (f, now = dayjs()) => {
  if (f.indefinite) return "";
  return untilError(f.until, now) || (f.from && f.from.isAfter(f.until, "day") ? FROM_AFTER_UNTIL_MESSAGE : "");
};

/**
 * 추가·수정·승인 요청 몸통의 기간 칸(D-144). 무기한이면 until 을 보내지 않는다.
 * 시작일을 비우면(옛 항목 수정·승인) 보내지 않는다 = 서버가 승인·등록 때부터로 본다
 */
export const periodBody = (original, f, now = dayjs()) => {
  const body = { indefinite: !!f.indefinite };
  if (f.from) body.from = fromBody(original && original.from, f.from);
  if (!f.indefinite) body.until = untilBody(original && original.until, f.until, now);
  return body;
};

const dayText = (iso) => dayjs(iso).format("YYYY-MM-DD");

/**
 * 표 「기간」 칸(D-144). range = "시작 ~ 끝", note = 남은 기간·시작 전, expired = 끝남.
 * 시작이 없는 옛 항목은 승인·등록 때부터라 「즉시」, 무기한은 끝나지 않는다
 */
export const periodOf = (row, now = dayjs()) => {
  const range = `${row.from ? dayText(row.from) : "즉시"} ~ ${
    row.indefinite ? "무기한" : row.until ? dayText(row.until) : "-"
  }`;
  if (row.from && dayjs(row.from).isAfter(now)) return { range, note: "시작 전", expired: false };
  if (row.indefinite) return { range, note: "", expired: false };
  const { expired, text } = remainOf(row.until, now);
  return { range, note: expired ? "" : text, expired };
};

// 신청·승인 상태(D-139). 고객사 신청은 대기로 들어오고, 운영자가 직접 넣으면 바로 승인
export const STATUS_TAG = {
  PENDING: { label: "대기", color: "orange" },
  APPROVED: { label: "승인", color: "green" },
  REJECTED: { label: "거절", color: "red" },
  CANCELED: { label: "취소" },
};

/** 상태가 없는 옛 항목(D-138)은 승인으로 본다 */
export const statusOf = (row) => (row && row.status) || "APPROVED";

// 목록 위 거르기 탭
export const FILTERS = [
  { key: "ALL", label: "전체" },
  { key: "PENDING", label: "대기", statuses: ["PENDING"] },
  { key: "APPROVED", label: "승인", statuses: ["APPROVED"] },
  { key: "CLOSED", label: "거절·취소", statuses: ["REJECTED", "CANCELED"] },
];

// ponytail: 목록 전체를 받아 화면에서 거른다(운영자가 관리하는 짧은 목록) — 길어지면 서버 ?status= 로
export const filterRows = (rows, key) => {
  const statuses = (FILTERS.find((f) => f.key === key) || {}).statuses;
  return statuses ? rows.filter((r) => statuses.includes(statusOf(r))) : rows;
};

/** 사람 칸: 이름(아이디), 이름이 없으면 아이디 */
export const personText = (p) => {
  if (!p) return "-";
  return p.name ? `${p.name}(${p.username})` : p.username || p.accountId || "-";
};
